import { useEffect, useRef, useState } from 'react';
import type { VehicleType } from '~/graphql/graphql';
import type { DomesticDeliveryMethod } from '~/types/general';
import type { VehicleOption } from '~/utils/vehicleGroups';

export type TransportMode = 'truck' | 'railcar' | 'container';

export function deliveryMethodToIncoTerm(
  deliveryMethod: DomesticDeliveryMethod
): 'EXW' | 'DAP' {
  return deliveryMethod === 'pickup' ? 'EXW' : 'DAP';
}

export function incoTermToDeliveryMethod(
  incoTerm: string | null | undefined
): DomesticDeliveryMethod | undefined {
  if (incoTerm === 'EXW') return 'pickup';
  if (incoTerm === 'DAP') return 'delivered';
  return undefined;
}

export function getActiveFacility<T>(
  deliveryMethod: DomesticDeliveryMethod,
  pickupFacility: T,
  sellerFacility: T
): T {
  return deliveryMethod === 'pickup' ? pickupFacility : sellerFacility;
}

export function vehicleTypeToTransportMode(
  vehicleType: VehicleType
): TransportMode {
  if (vehicleType === 'DOMESTIC_TRUCK') return 'truck';
  if (vehicleType === 'INTERNATIONAL_CONTAINER') return 'container';
  return 'railcar';
}

export type TransportChangeHandler = (
  newDeliveryMethod: DomesticDeliveryMethod,
  newTransportVehicleId: string,
  vehicleType?: VehicleType
) => void;

export function applyTransportChange(
  newDeliveryMethod: DomesticDeliveryMethod,
  newTransportVehicleId: string,
  vehicleType: VehicleType | undefined,
  setDeliveryMethod: (m: DomesticDeliveryMethod) => void,
  setSelectedTransportVehicleId: (id: string) => void,
  setTransport: (mode: TransportMode) => void
) {
  setDeliveryMethod(newDeliveryMethod);
  setSelectedTransportVehicleId(newTransportVehicleId);
  if (vehicleType) {
    setTransport(vehicleTypeToTransportMode(vehicleType));
  }
}

export interface InitialTransportState {
  deliveryMethod?: DomesticDeliveryMethod;
  selectedTransportVehicleId?: string;
  transport?: TransportMode;
}

export function useTransportState(
  onTransportChange?: TransportChangeHandler,
  initial?: InitialTransportState
) {
  const [transport, setTransport] = useState<TransportMode>(
    initial?.transport ?? 'truck'
  );
  const [deliveryMethod, setDeliveryMethod] = useState<DomesticDeliveryMethod>(
    initial?.deliveryMethod ?? 'delivered'
  );
  const [selectedTransportVehicleId, setSelectedTransportVehicleId] = useState(
    initial?.selectedTransportVehicleId ?? ''
  );

  const handleTransportChange: TransportChangeHandler = (
    newDeliveryMethod,
    newTransportVehicleId,
    vehicleType
  ) => {
    applyTransportChange(
      newDeliveryMethod,
      newTransportVehicleId,
      vehicleType,
      setDeliveryMethod,
      setSelectedTransportVehicleId,
      setTransport
    );
    onTransportChange?.(newDeliveryMethod, newTransportVehicleId, vehicleType);
  };

  const resetTransport = () => {
    setDeliveryMethod('delivered');
    setSelectedTransportVehicleId('');
    setTransport('truck');
  };

  return {
    transport,
    deliveryMethod,
    selectedTransportVehicleId,
    handleTransportChange,
    resetTransport,
  };
}

export function useAutoSelectTransportVehicle(
  availableVehicles: VehicleOption[] | undefined,
  selectedTransportVehicleId: string,
  handleTransportChange: TransportChangeHandler,
  deliveryMethod: DomesticDeliveryMethod
) {
  const pendingAutoSelectVehicleId = useRef<string | null>(null);

  useEffect(() => {
    if (!availableVehicles || availableVehicles.length === 0) return;
    if (selectedTransportVehicleId) return;

    const firstVehicle = availableVehicles[0];
    if (!firstVehicle) return;
    if (pendingAutoSelectVehicleId.current === firstVehicle.id) return;

    pendingAutoSelectVehicleId.current = firstVehicle.id;
    handleTransportChange(
      deliveryMethod,
      firstVehicle.id,
      firstVehicle.vehicleType
    );
  }, [
    availableVehicles,
    selectedTransportVehicleId,
    handleTransportChange,
    deliveryMethod,
  ]);
}
